import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useMonitorData } from './hooks/useMonitorData';
import { useModelStats } from './hooks/useModelStats';
import { useFloorMusic } from './hooks/useFloorMusic';
import { TradeDialogData } from './types/tradingFloor.types';
import { CANVAS_WIDTH, CANVAS_HEIGHT } from './constants/officeLayout';
import PixiOffice from './components/PixiOffice';
import TradeDialog from './components/TradeDialog';
import './TradingFloor.css';

const TradingFloor: React.FC = () => {
  const { snapshot, cycleSummary, isConnected } = useMonitorData();
  const { statsMap, daySummary } = useModelStats();
  const { toggle: toggleMusic, isPlayingRef: isMusicPlaying } = useFloorMusic();
  const [dialogData, setDialogData] = useState<TradeDialogData | null>(null);
  const [musicOn, setMusicOn] = useState(false);
  const pageRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const handleAvatarClick = useCallback((data: TradeDialogData) => {
    setDialogData({
      ...data,
      modelStats: statsMap[data.modelName] || null,
      daySummary: data.isManager ? daySummary : null,
    });
  }, [statsMap, daySummary]);

  const handleCloseDialog = useCallback(() => {
    setDialogData(null);
  }, []);

  // CSS-based fullscreen toggle (works on all browsers including iOS Safari)
  const toggleFullscreen = useCallback(() => {
    setIsFullscreen(prev => !prev);
  }, []);

  const handleMusicToggle = useCallback(() => {
    const nowPlaying = toggleMusic();
    setMusicOn(nowPlaying);
  }, [toggleMusic]);

  // Responsive scaling: fit canvas to available space
  useEffect(() => {
    const updateScale = () => {
      const screenW = window.innerWidth;
      const screenH = window.innerHeight;
      const scaleW = screenW / CANVAS_WIDTH;
      const scaleH = screenH / CANVAS_HEIGHT;
      setScale(Math.min(1, scaleW, scaleH));
    };
    updateScale();
    window.addEventListener('resize', updateScale);
    window.addEventListener('orientationchange', () => setTimeout(updateScale, 150));
    return () => {
      window.removeEventListener('resize', updateScale);
    };
  }, []);

  const scaledW = CANVAS_WIDTH * scale;
  const scaledH = CANVAS_HEIGHT * scale;

  return (
    <div
      ref={pageRef}
      className={`trading-floor-page ${isFullscreen ? 'is-fullscreen' : ''}`}
    >
      {/* Status bar — hidden in fullscreen */}
      {!isFullscreen && (
        <div className="floor-status-bar">
          <div className="floor-status-left">
            <span className={`floor-status-dot ${isConnected ? 'connected' : 'disconnected'}`} />
            <span className="floor-status-title">TRADING FLOOR</span>
          </div>
          {snapshot && (
            <div className="floor-status-right">
              <span className="floor-status-positions">
                {snapshot.totalPositions} active
              </span>
              <span className={`floor-status-pl ${snapshot.totalPL >= 0 ? 'profit' : 'loss'}`}>
                P/L: ${snapshot.totalPL.toFixed(2)}
              </span>
            </div>
          )}
        </div>
      )}

      {/* Canvas wrapper */}
      <div
        className="floor-canvas-wrapper"
        style={{ width: scaledW, height: scaledH }}
      >
        {/* Scaling layer — 0x0 layout box so parent overflow:hidden clips correctly */}
        <div
          style={{
            width: 0,
            height: 0,
            overflow: 'visible',
            transform: `scale(${scale})`,
            transformOrigin: 'top left',
          }}
        >
          <div
            className="floor-canvas-container"
            style={{ width: CANVAS_WIDTH, height: CANVAS_HEIGHT }}
          >
            <PixiOffice
              snapshot={snapshot}
              cycleSummary={cycleSummary}
              onAvatarClick={handleAvatarClick}
            />
            {dialogData && (
              <TradeDialog data={dialogData} onClose={handleCloseDialog} />
            )}
          </div>
        </div>

        {/* Music toggle button */}
        <button
          className="floor-music-btn"
          onClick={handleMusicToggle}
          title={musicOn ? 'Mute music' : 'Play music'}
        >
          {musicOn ? '\u266B' : '\u266A'}
        </button>

        {/* Fullscreen toggle button */}
        <button
          className="floor-fullscreen-btn"
          onClick={toggleFullscreen}
          title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
        >
          {isFullscreen ? '\u2716' : '\u26F6'}
        </button>
      </div>

      {/* Legend — hidden in fullscreen */}
      {!isFullscreen && (
        <div className="floor-legend">
          <div className="floor-legend-item">
            <span className="legend-dot idle" /> Idle
          </div>
          <div className="floor-legend-item">
            <span className="legend-dot alert" /> New Trade
          </div>
          <div className="floor-legend-item">
            <span className="legend-dot profit" /> In Profit
          </div>
          <div className="floor-legend-item">
            <span className="legend-dot celebrating" /> Big Profit
          </div>
          <div className="floor-legend-item">
            <span className="legend-dot stressed" /> Losing
          </div>
          <div className="floor-legend-item">
            <span className="legend-dot slumped" /> Stopped Out
          </div>
        </div>
      )}
    </div>
  );
};

export default TradingFloor;
